// แบบพิมพ์การเสนอขอพระราชทานเครื่องราชอิสริยาภรณ์ของสถานศึกษา (หน้า /college/decoration)
// เปิดหน้าต่างใหม่ → ผู้ใช้สั่งพิมพ์/บันทึก PDF จากเบราว์เซอร์ (แนวเดียวกับ printReport ของหน้าค่าตอบแทน)
//   printQualificationList  บัญชีแสดงคุณสมบัติ แยกประเภทบุคลากร / ชั้นต่ำกว่าสายสะพาย-ชั้นสายสะพาย / ผู้เกษียณ (ข้อ ๑๓)
//   printClassSummary       บัญชีแสดงจำนวนชั้นตราที่ขอ จำแนกชาย-หญิง
//   printSelfCertify        แบบรับรองคุณสมบัติตนเอง (ข้อ ๑๒ วรรคสอง) คนละ ๑ หน้า
import { CERTIFY_ITEMS, CLASSES, className, isSash, thaiDate, thaiDateLong } from "@/utils/decorationClasses";

const TYPE_ORDER = ["teacher", "civil", "govem", "permanent"];
const TYPE_TITLE = {
  teacher: "ข้าราชการครูและบุคลากรทางการศึกษา",
  civil: "ข้าราชการ (บุคลากรทางการศึกษาอื่นตามมาตรา ๓๘ ค.(๒))",
  govem: "พนักงานราชการ",
  permanent: "ลูกจ้างประจำ"
};
const START_LABEL = { teacher: "เริ่มรับราชการ", civil: "เริ่มรับราชการ", govem: "เริ่มจ้าง", permanent: "เริ่มจ้าง" };

const esc = s =>
  String(s === null || s === undefined ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
const thaiNum = s => String(s).replace(/[0-9]/g, d => "๐๑๒๓๔๕๖๗๘๙"[d]);
const money = v => (v ? Number(v).toLocaleString("th-TH") : "-");

function openPrint(title, body, landscape) {
  const w = window.open("", "_blank");
  if (!w) {
    alert("เบราว์เซอร์บล็อกหน้าต่างใหม่ — อนุญาต pop-up ของเว็บไซต์นี้แล้วลองอีกครั้ง");
    return;
  }
  w.document.write(`<!doctype html><html lang="th"><head><meta charset="utf-8"><title>${esc(title)}</title>
<link href="https://fonts.googleapis.com/css2?family=Sarabun:wght@400;700&display=swap" rel="stylesheet">
<style>
  @page { size: A4 ${landscape ? "landscape" : "portrait"}; margin: 14mm 12mm; }
  body { font-family: "TH Sarabun New", "Sarabun", sans-serif; font-size: 15px; color: #000; margin: 0; }
  h1 { font-size: 19px; text-align: center; margin: 0 0 2px; }
  .sub { text-align: center; margin-bottom: 8px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
  th, td { border: 1px solid #000; padding: 3px 5px; vertical-align: top; }
  th { background: #f0f0f0; font-weight: 700; text-align: center; }
  td.c { text-align: center; } td.r { text-align: right; }
  .section { page-break-after: always; }
  .section:last-child { page-break-after: auto; }
  .sign { margin-top: 18px; margin-left: 55%; text-align: center; line-height: 1.9; }
  .cert { margin: 10px 0 0; text-indent: 2.5em; line-height: 1.7; }
  .note { font-size: 13px; color: #333; }
  .items li { margin: 4px 0; }
  .box { display: inline-block; width: 12px; height: 12px; border: 1px solid #000; margin-right: 6px; vertical-align: -1px; }
  @media print { .noprint { display: none; } }
</style></head><body>
<div class="noprint" style="padding:8px;background:#fffbe6;border-bottom:1px solid #e0c060">
  ตรวจข้อมูลให้ถูกต้องก่อนพิมพ์ · <button onclick="window.print()">พิมพ์ / บันทึก PDF</button>
</div>
${body}</body></html>`);
  w.document.close();
}

function decoText(decorations) {
  const list = (decorations || []).filter(d => d.class);
  if (!list.length) return "-";
  return list
    .slice()
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    .map(d => `${esc(d.class)} ${esc(thaiDate(d.date))}`)
    .join("<br>");
}

function signBlock(lines) {
  return `<div class="sign">(ลงชื่อ)..................................................<br>
    (..................................................)<br>${lines.map(esc).join("<br>")}</div>`;
}

/**
 * requests: decoration_request + snapshot (จาก API overview)
 * ctx: { year, collegeName }
 */
export function printQualificationList(requests, ctx) {
  const groups = {};
  requests.forEach(r => {
    const s = r.snapshot || {};
    const retiring = s.retire_year && Number(s.retire_year) === Number(ctx.year);
    const key = `${r.person_type}|${isSash(r.request_class) ? 1 : 0}|${retiring ? 1 : 0}`;
    (groups[key] = groups[key] || []).push(r);
  });
  const keys = Object.keys(groups).sort((a, b) => {
    const [ta, sa, ra] = a.split("|");
    const [tb, sb, rb] = b.split("|");
    return TYPE_ORDER.indexOf(ta) - TYPE_ORDER.indexOf(tb) || ra - rb || sa - sb;
  });
  const sections = keys.map(key => {
    const [type, sash, retiring] = key.split("|");
    const rows = groups[key]
      .slice()
      .sort((a, b) => {
        const ia = CLASSES.findIndex(c => c.abbr === a.request_class);
        const ib = CLASSES.findIndex(c => c.abbr === b.request_class);
        return ib - ia || String(a.snapshot.full_name).localeCompare(String(b.snapshot.full_name), "th");
      })
      .map((r, i) => {
        const s = r.snapshot || {};
        return `<tr><td class="c">${thaiNum(i + 1)}</td><td>${esc(s.full_name)}</td>
          <td class="c">${esc(thaiDate(s.start_date))}</td>
          <td>${esc(s.position_name)}${s.rang ? "<br>" + esc(s.rang) : ""}${s.id_position ? "<br>ตำแหน่งเลขที่ " + esc(s.id_position) : ""}</td>
          <td class="r">${money(s.salary_s)}</td>
          <td>${decoText(s.decorations)}</td>
          <td class="c"><b>${esc(r.request_class)}</b><br><span class="note">${esc(className(r.request_class))}</span></td>
          <td class="note">${esc(s.category_ref || "")}${r.note ? "<br>" + esc(r.note) : ""}</td></tr>`;
      })
      .join("");
    const title =
      `บัญชีแสดงคุณสมบัติของ${TYPE_TITLE[type] || type} ซึ่งเสนอขอพระราชทานเครื่องราชอิสริยาภรณ์ ` +
      `${sash === "1" ? "ชั้นสายสะพาย" : "ชั้นต่ำกว่าสายสะพาย"} ประจำปี พ.ศ. ${thaiNum(ctx.year)}` +
      (retiring === "1" ? " (ผู้เกษียณอายุราชการ)" : "");
    return `<div class="section"><h1>${esc(title)}</h1>
      <div class="sub">${esc(ctx.collegeName)} สังกัดสำนักงานคณะกรรมการการอาชีวศึกษา กระทรวงศึกษาธิการ</div>
      <table><thead><tr><th style="width:4%">ลำดับ</th><th style="width:17%">ชื่อตัว - ชื่อสกุล</th>
        <th style="width:10%">วัน เดือน ปี<br>${START_LABEL[type] || "เริ่มรับราชการ"}</th>
        <th style="width:22%">ตำแหน่ง / ระดับ</th><th style="width:8%">เงินเดือน<br>(บาท)</th>
        <th style="width:13%">เครื่องราชฯ ที่ได้รับ<br>(ชั้นตรา / วันที่)</th><th style="width:10%">ขอครั้งนี้</th>
        <th>หมายเหตุ</th></tr></thead><tbody>${rows}</tbody></table>
      <div class="cert">ขอรับรองว่าบุคคลตามบัญชีนี้เป็นผู้มีคุณสมบัติครบถ้วนตามระเบียบสำนักนายกรัฐมนตรีว่าด้วยการขอพระราชทาน
        เครื่องราชอิสริยาภรณ์อันเป็นที่เชิดชูยิ่งช้างเผือกและเครื่องราชอิสริยาภรณ์อันมีเกียรติยศยิ่งมงกุฎไทย พ.ศ. ๒๕๖๔
        และได้รับรองคุณสมบัติของตนเองตามข้อ ๑๒ แล้ว รวม ${thaiNum(groups[key].length)} ราย</div>
      ${signBlock(["ผู้อำนวยการ" + ctx.collegeName, "ผู้รับรอง"])}</div>`;
  });
  openPrint(`บัญชีแสดงคุณสมบัติ ${ctx.year}`, sections.join("") || "<p>ยังไม่มีรายชื่อที่เสนอขอ</p>", true);
}

export function printClassSummary(requests, ctx) {
  const types = TYPE_ORDER.filter(t => requests.some(r => r.person_type === t));
  const used = CLASSES.filter(c => requests.some(r => r.request_class === c.abbr)).reverse();
  const count = (cls, type, sex) =>
    requests.filter(
      r => r.request_class === cls && (!type || r.person_type === type) && (!sex || (r.snapshot || {}).sex === sex)
    ).length;
  const head = types.map(t => `<th colspan="3">${esc(TYPE_TITLE[t])}</th>`).join("");
  const sub = types.map(() => "<th>ชาย</th><th>หญิง</th><th>รวม</th>").join("");
  const row = (label, cls) =>
    `<tr><td>${label}</td>${types
      .map(t => {
        const m = cls ? count(cls, t, "ชาย") : requests.filter(r => r.person_type === t && (r.snapshot || {}).sex === "ชาย").length;
        const f = cls ? count(cls, t, "หญิง") : requests.filter(r => r.person_type === t && (r.snapshot || {}).sex === "หญิง").length;
        const all = cls ? count(cls, t) : requests.filter(r => r.person_type === t).length;
        return `<td class="c">${thaiNum(m)}</td><td class="c">${thaiNum(f)}</td><td class="c"><b>${thaiNum(all)}</b></td>`;
      })
      .join("")}<td class="c"><b>${thaiNum(cls ? count(cls) : requests.length)}</b></td></tr>`;
  const body = `<h1>บัญชีแสดงจำนวนชั้นตราเครื่องราชอิสริยาภรณ์ที่เสนอขอพระราชทาน ประจำปี พ.ศ. ${thaiNum(ctx.year)}</h1>
    <div class="sub">${esc(ctx.collegeName)} สังกัดสำนักงานคณะกรรมการการอาชีวศึกษา</div>
    <table><thead><tr><th rowspan="2">ชั้นตรา</th>${head}<th rowspan="2">รวมทั้งสิ้น</th></tr><tr>${sub}</tr></thead>
    <tbody>${used.map(c => row(`${esc(c.name)} (${esc(c.abbr)})`, c.abbr)).join("")}
    ${row("<b>รวม</b>", null)}</tbody></table>
    <div class="note">เพศจำแนกจากคำนำหน้าชื่อเมื่อทะเบียนประวัติไม่ระบุเพศ — ตรวจสอบก่อนส่ง</div>
    ${signBlock(["ผู้อำนวยการ" + ctx.collegeName])}`;
  openPrint(`จำนวนชั้นตรา ${ctx.year}`, body, true);
}

export function printSelfCertify(requests, ctx) {
  const pages = requests.map(r => {
    const s = r.snapshot || {};
    const items = CERTIFY_ITEMS.filter(i => i.key !== "self_signed")
      .map(i => `<li><span class="box"></span>${esc(i.text)} <span class="note">(${esc(i.ref)})</span></li>`)
      .join("");
    return `<div class="section"><h1>แบบรับรองคุณสมบัติของผู้ซึ่งได้รับการเสนอขอพระราชทานเครื่องราชอิสริยาภรณ์</h1>
      <div class="sub">ประจำปี พ.ศ. ${thaiNum(ctx.year)}</div>
      <p class="cert">ข้าพเจ้า ${esc(s.full_name)} ตำแหน่ง ${esc(s.position_name)}${s.rang ? " " + esc(s.rang) : ""}
        สังกัด ${esc(ctx.collegeName)} สำนักงานคณะกรรมการการอาชีวศึกษา ซึ่งได้รับการเสนอขอพระราชทาน
        ${esc(className(r.request_class))} (${esc(r.request_class)}) ขอรับรองว่าข้าพเจ้าเป็นผู้มีคุณสมบัติและไม่มีลักษณะต้องห้าม
        ตามข้อ ๑๒ แห่งระเบียบสำนักนายกรัฐมนตรีว่าด้วยการขอพระราชทานเครื่องราชอิสริยาภรณ์อันเป็นที่เชิดชูยิ่งช้างเผือก
        และเครื่องราชอิสริยาภรณ์อันมีเกียรติยศยิ่งมงกุฎไทย พ.ศ. ๒๕๖๔ ดังนี้</p>
      <ul class="items" style="list-style:none">${items}</ul>
      <p class="cert">หากปรากฏภายหลังว่าข้อความที่รับรองไม่เป็นความจริง ข้าพเจ้ายินยอมให้ดำเนินการตามระเบียบ</p>
      ${signBlock(["ผู้รับรอง", "วันที่ ........ เดือน .................... พ.ศ. ........"])}
      <p class="note" style="margin-top:24px">ประวัติเครื่องราชฯ ที่ได้รับแล้ว: ${decoText(s.decorations).replace(/<br>/g, ", ")}
        · ${START_LABEL[r.person_type] || "เริ่มรับราชการ"} ${esc(thaiDateLong(s.start_date))}</p></div>`;
  });
  openPrint(`แบบรับรองคุณสมบัติ ${ctx.year}`, pages.join("") || "<p>ยังไม่มีรายชื่อที่เสนอขอ</p>", false);
}
